import { DEMO_ACCOUNTS, type DemoAccount, type DemoAccountCurrency } from "./accounts.ts";
import { DEMO_CONTACTS, type DemoContact } from "./contacts.ts";
import { type DemoCompanyId } from "./identifiers.ts";

export type DemoTransaction = {
  id: string;
  accountId: string;
  bookedAt: string;
  valueAt: string;
  amount: number;
  currency: DemoAccountCurrency;
  description: string;
  variableSymbol?: string;
  constantSymbol?: string;
  specificSymbol?: string;
  // Foreign keys
  counterpartyId: string | "internal-payroll" | "internal-fx" | "internal-fee";
  counterpartyName: string;
  counterpartyAccount: string;
  counterpartyBic?: string;
  companyId: DemoCompanyId;
  // UI mapping — matches transactionCategoryEnum (@dfp/bw-isomorphic-enums).
  category:
    | "INCOME"
    | "PRODUCTION"
    | "SALES"
    | "OPERATIONS"
    | "WAGES"
    | "TRAVEL"
    | "INVESTMENTS"
    | "FINANCIAL"
    | "TAXES"
    | "INTERNAL"
    | "OTHER";
  method: "TRANSFER" | "CARD" | "DIRECT" | "STANDING" | "EXCHANGE" | "FEE";
  direction: "IN" | "OUT";
  status: "Booked";
};

// Anchor to the REAL current date at module load so demo data always lands
// inside the default analytics/balance windows (last 30/90 days). Using a
// hardcoded anchor would push all transactions into the past relative to
// "today", leaving the home-dashboard 30-day balance chart flat/empty.
const ANCHOR = new Date();
function shift(daysAgo: number, hour = 9, minute = 0): string {
  const d = new Date(ANCHOR);
  d.setUTCDate(d.getUTCDate() - daysAgo);
  d.setUTCHours(hour, minute, 0, 0);
  return d.toISOString();
}

function findAccount(companyId: DemoCompanyId, currency: DemoAccountCurrency, hint?: string): DemoAccount {
  const candidates = DEMO_ACCOUNTS.filter(
    (a) => a.companyId === companyId && a.currency === currency,
  );
  if (hint) {
    const match = candidates.find((a) => a.name.toLowerCase().includes(hint.toLowerCase()));
    if (match) return match;
  }
  return candidates[0] ?? DEMO_ACCOUNTS.find((a) => a.companyId === companyId)!;
}

function contact(id: string): DemoContact {
  const c = DEMO_CONTACTS.find((x) => x.id === id);
  if (!c) throw new Error(`unknown contact ${id}`);
  return c;
}

type Spend = {
  contactId: string;
  amounts: number[];
  daysAgo: number[];
  description: string;
  currency: DemoAccountCurrency;
  category: DemoTransaction["category"];
  method: DemoTransaction["method"];
  hour?: number;
  accountHint?: string;
};

// All flows below are crafted manually so the timeline tells a story:
// monthly rent + payroll + SaaS subs, weekly office supplies, quarterly
// audit/tax, client invoices, payment-gateway settlements, FX conversions.
const SPENDS: Spend[] = [
  // Brickline rent (Ventures office)
  { contactId: "demo-contact-brickline", amounts: [12_400, 12_400, 12_400], daysAgo: [20, 50, 80], description: "Office rent — Friedrichstraße 90", currency: "EUR", category: "OPERATIONS", method: "STANDING", accountHint: "Operating" },
  // Vexa Cloud (Holdings SaaS)
  { contactId: "demo-contact-vexa", amounts: [6_080, 6_080, 6_080], daysAgo: [16, 46, 76], description: "Vexa Cloud — infrastructure", currency: "EUR", category: "OPERATIONS", method: "CARD", accountHint: "Revolut" },
  // Pixelpath (DE design)
  { contactId: "demo-contact-pixelpath", amounts: [1_530, 1_530, 1_530], daysAgo: [4, 34, 64], description: "Pixelpath Studio — design seats", currency: "EUR", category: "OPERATIONS", method: "CARD", accountHint: "Wise" },
  // Tetra Drive (Labs)
  { contactId: "demo-contact-tetra-drive", amounts: [4_280, 4_280, 4_280], daysAgo: [12, 42, 72], description: "Tetra Drive — DevOps platform", currency: "PLN", category: "OPERATIONS", method: "STANDING", accountHint: "Operating" },
  // Quillstream UK
  { contactId: "demo-contact-quillstream", amounts: [620, 620], daysAgo: [9, 39], description: "Quillstream — editorial workspace", currency: "GBP", category: "OPERATIONS", method: "CARD", accountHint: "Operating" },
  // Halofield identity
  { contactId: "demo-contact-halofield", amounts: [3_280, 3_280, 3_280], daysAgo: [11, 41, 71], description: "Halofield Systems — identity provider", currency: "EUR", category: "OPERATIONS", method: "STANDING", accountHint: "Operating" },
  // Beampoint
  { contactId: "demo-contact-beampoint", amounts: [1_960, 1_960, 1_960], daysAgo: [7, 37, 67], description: "Beampoint Analytics — pro plan", currency: "GBP", category: "OPERATIONS", method: "CARD", accountHint: "Operating" },

  // Energy / telecom
  { contactId: "demo-contact-aurea", amounts: [22_410, 18_870], daysAgo: [18, 48], description: "Aurea Power — electricity", currency: "CZK", category: "OPERATIONS", method: "DIRECT", accountHint: "Provozní" },
  { contactId: "demo-contact-mintway", amounts: [1_240, 1_240, 1_240], daysAgo: [10, 40, 70], description: "Mintway Telecom — corporate tariffs", currency: "EUR", category: "OPERATIONS", method: "DIRECT", accountHint: "Operating" },

  // Office supplies (weekly small)
  { contactId: "demo-contact-solune", amounts: [2_140, 1_860, 2_400], daysAgo: [5, 12, 26], description: "Solune Office Supplies — kitchen + paper", currency: "PLN", category: "OPERATIONS", method: "CARD", accountHint: "Operating" },

  // Logistics
  { contactId: "demo-contact-aerial-cargo", amounts: [2_840, 2_180, 2_820], daysAgo: [8, 22, 50], description: "Aerial Cargo — outbound shipments", currency: "EUR", category: "OPERATIONS", method: "TRANSFER", accountHint: "Operating" },
  { contactId: "demo-contact-coastward", amounts: [4_280, 3_560, 5_000], daysAgo: [8, 22, 50], description: "Coastward Logistics — domestic delivery", currency: "CZK", category: "OPERATIONS", method: "TRANSFER", accountHint: "Provozní" },
  { contactId: "demo-contact-aerlite-travel", amounts: [3_120, 2_840, 3_280], daysAgo: [24, 38, 64], description: "Aerlite Travel — business trips", currency: "EUR", category: "TRAVEL", method: "CARD", accountHint: "Operating" },

  // Professional
  { contactId: "demo-contact-northpoint-advisors", amounts: [7_500, 7_000], daysAgo: [9, 39], description: "Northpoint Advisors — legal retainer", currency: "GBP", category: "OPERATIONS", method: "TRANSFER", accountHint: "Operating" },
  { contactId: "demo-contact-vanguard-accounting", amounts: [2_280, 2_280, 2_280], daysAgo: [15, 45, 75], description: "Vanguard Accounting — monthly closing", currency: "EUR", category: "OPERATIONS", method: "TRANSFER", accountHint: "Operating" },
  { contactId: "demo-contact-plumb-audit", amounts: [22_400], daysAgo: [60], description: "Plumb Audit — annual audit fee", currency: "GBP", category: "OPERATIONS", method: "TRANSFER", accountHint: "Operating" },
  { contactId: "demo-contact-brightcircle-insurance", amounts: [2_060, 2_060, 2_060, 2_060], daysAgo: [3, 33, 63, 93], description: "BrightCircle — D&O policy premium", currency: "EUR", category: "FINANCIAL", method: "STANDING", accountHint: "Operating" },

  // Marketing
  { contactId: "demo-contact-sparrow-media", amounts: [6_100, 6_100, 6_000], daysAgo: [7, 37, 67], description: "Sparrow Media — Q2 campaigns", currency: "EUR", category: "SALES", method: "TRANSFER", accountHint: "Operating" },
  { contactId: "demo-contact-tilly-creative", amounts: [42_000, 40_300, 40_000], daysAgo: [3, 28, 58], description: "Tilly Creative — brand sprint", currency: "CZK", category: "SALES", method: "TRANSFER", accountHint: "Provozní" },
  { contactId: "demo-contact-halo-studios", amounts: [4_100, 4_100, 4_200], daysAgo: [13, 43, 73], description: "Halo Studios — video production", currency: "EUR", category: "SALES", method: "TRANSFER", accountHint: "Operating" },

  // Income — a few named monthly client invoices kept for contact/category
  // realism. The BULK of the inflow is generated as a frequent near-daily
  // stream in pushStreamRows() below (gateway settlements + smaller
  // invoices), so the analytics chart shows steady green activity on most days
  // rather than 1–3 large monthly lumps.
  { contactId: "demo-contact-northpoint-capital", amounts: [54_000, 52_000, 53_000], daysAgo: [4, 34, 64], description: "Northpoint Capital — monthly retainer", currency: "EUR", category: "INCOME", method: "TRANSFER", accountHint: "Operating" },
  { contactId: "demo-contact-pelegrini-hospitality", amounts: [620_000, 610_000, 615_000], daysAgo: [6, 36, 66], description: "Pelegrini Hospitality — monthly programme", currency: "CZK", category: "INCOME", method: "TRANSFER", accountHint: "Provozní" },
  { contactId: "demo-contact-kepler-robotics", amounts: [260_000, 255_000, 258_000], daysAgo: [9, 39, 69], description: "Kepler Robotics — R&D milestone", currency: "PLN", category: "INCOME", method: "TRANSFER", accountHint: "Operating" },
  { contactId: "demo-contact-brightsky-media", amounts: [38_000, 36_000, 37_000], daysAgo: [10, 40, 70], description: "BrightSky Media — campaign invoice", currency: "EUR", category: "INCOME", method: "TRANSFER", accountHint: "Operating" },
  { contactId: "demo-contact-mirage-apparel", amounts: [22_000, 21_000, 21_500], daysAgo: [7, 37, 67], description: "Mirage Apparel — licensing royalties", currency: "GBP", category: "INCOME", method: "TRANSFER", accountHint: "Operating" },

  // FX bridge fees
  { contactId: "demo-contact-fxbridge-treasury", amounts: [320, 280, 410], daysAgo: [9, 24, 52], description: "FXBridge — conversion fee", currency: "EUR", category: "FINANCIAL", method: "FEE", accountHint: "Treasury" },
];

// Synthetic payroll runs (multi-company, multi-currency).
type Payroll = {
  companyId: DemoCompanyId;
  currency: DemoAccountCurrency;
  amount: number;
  daysAgo: number;
  description: string;
};

// Payroll is paid WEEKLY (one run every 7 days across the whole window) at
// roughly monthly_total / 4.33 each. Weekly runs keep the WAGES cost smooth and
// modest per week — a single semi-monthly lump used to tower over one week's
// income and make the cockpit cashflow bar read red there. Monthly totals (and
// therefore balances) are unchanged; only the distribution is smoother, so
// weekly income stays above costs in nearly every period. Realistic: many SMEs
// run weekly/bi-weekly payroll.
type PayrollPlan = {
  companyId: DemoCompanyId;
  currency: DemoAccountCurrency;
  monthlyTotal: number;
  description: string;
};
const PAYROLL_PLANS: PayrollPlan[] = [
  { companyId: "demo-company-holdings-at", currency: "EUR", monthlyTotal: 177_000, description: "Payroll run — Contoso Holdings" },
  { companyId: "demo-company-studio-cz", currency: "CZK", monthlyTotal: 2_510_000, description: "Mzdy — Contoso Studio" },
  { companyId: "demo-company-labs-pl", currency: "PLN", monthlyTotal: 558_000, description: "Wynagrodzenia — Contoso Labs" },
  { companyId: "demo-company-ventures-de", currency: "EUR", monthlyTotal: 84_000, description: "Gehälter — Contoso Ventures" },
  { companyId: "demo-company-trade-gb", currency: "GBP", monthlyTotal: 38_000, description: "Payroll run — Contoso Trade" },
];

function buildPayrolls(): Payroll[] {
  const out: Payroll[] = [];
  for (const plan of PAYROLL_PLANS) {
    // monthly / (30/7) ≈ weekly. Vary ±4% deterministically so the runs aren't
    // identical, then round to a nice amount.
    const weeklyBase = (plan.monthlyTotal * 7) / 30;
    const rng = makeRng(hashSeed(plan.companyId + "-pay"));
    // Pay every 7 days, offset by 6 so a run lands in the most recent week too.
    // Runs span the whole ~12-month window so WAGES shows in older months too.
    for (let daysAgo = 6; daysAgo <= 364; daysAgo += 7) {
      const amount = niceAmount(weeklyBase * (0.96 + rng() * 0.08), plan.currency);
      out.push({
        companyId: plan.companyId,
        currency: plan.currency,
        amount,
        daysAgo,
        description: plan.description,
      });
    }
  }
  return out;
}
const PAYROLLS: Payroll[] = buildPayrolls();

// Quarterly tax + audit
const TAXES: { companyId: DemoCompanyId; currency: DemoAccountCurrency; amount: number; daysAgo: number; description: string }[] = [
  { companyId: "demo-company-holdings-at", currency: "EUR", amount: 88_000, daysAgo: 40, description: "VAT settlement Q1 2026 — Contoso Holdings" },
  { companyId: "demo-company-studio-cz", currency: "CZK", amount: 824_000, daysAgo: 40, description: "DPH Q1 2026 — Contoso Studio" },
  { companyId: "demo-company-labs-pl", currency: "PLN", amount: 218_000, daysAgo: 40, description: "VAT Q1 2026 — Contoso Labs" },
  { companyId: "demo-company-ventures-de", currency: "EUR", amount: 52_400, daysAgo: 40, description: "Umsatzsteuer Q1 2026 — Contoso Ventures" },
  { companyId: "demo-company-trade-gb", currency: "GBP", amount: 28_200, daysAgo: 60, description: "Corporation tax instalment — Contoso Trade" },
];

// --- Frequent, near-daily activity streams -------------------------------
//
// To make the cockpit Analytics read as a BUSY, STEADY, profitable operation
// at daily/weekly granularity (not 1–3 monthly lumps), we synthesise many
// small, varied income and expense transactions across the whole ~90-day
// window. Amounts are deterministic (seeded PRNG) so the build is stable.

// Tiny seeded PRNG (mulberry32) — deterministic across builds.
function makeRng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// Round to a "nice" looking amount (varied, not identical, no trailing noise).
function niceAmount(value: number, currency: DemoAccountCurrency): number {
  const step = currency === "CZK" ? 500 : currency === "PLN" ? 50 : 10;
  return Math.max(step, Math.round(value / step) * step);
}

// One slice of a company's income/expense mix. `weight` controls how often
// this category is picked; `factor` scales the per-transaction amount relative
// to the stream mean; `names` is the pool of inline counterparties (chosen so
// the drill-down popover shows a plausible party for the category).
type CategoryMixSlice = {
  weight: number;
  factor: number;
  category: DemoTransaction["category"];
  names: string[];
};

type StreamConfig = {
  companyId: DemoCompanyId;
  currency: DemoAccountCurrency;
  accountHint: string;
  // income contacts to attribute inflows to (cycled through)
  incomeContactIds: string[];
  incomeDescriptions: string[];
  incomeMean: number; // native currency, mean per transaction
  // expense contacts to attribute card/supplier spend to (cycled through)
  expenseContactIds: string[];
  expenseDescriptions: string[];
  expenseMean: number;
  // Optional: spread the daily income/expense stream across several categories
  // with matching inline counterparties (instead of a single bucket). Used for
  // the company surfaced in the cockpit analytics view so BOTH its income and
  // cost category breakdowns look like a real, diversified business. Total
  // volume is preserved (factors average ~1).
  incomeMix?: CategoryMixSlice[];
  expenseMix?: CategoryMixSlice[];
};

// Per-company near-daily streams. Means tuned (see fx rates in mockHandlers
// buildExchangeRates) so total inflow runs ~1.25–1.4× total outflow in the
// EUR analytics view, while payroll/rent/tax stay as occasional larger items.
const STREAMS: StreamConfig[] = [
  {
    companyId: "demo-company-holdings-at",
    currency: "EUR",
    accountHint: "Operating",
    incomeContactIds: ["demo-contact-northpoint-capital", "demo-contact-marisco-industries"],
    incomeDescriptions: ["Client settlement", "Services invoice", "Advisory fee", "Milestone payment", "Subscription revenue"],
    // Tuned so weekly inflow comfortably exceeds the (now diversified) weekly
    // costs — a healthy ~25–35% operating margin in every full week.
    incomeMean: 11_000,
    // Holdings' inflow is also spread across income-side categories so the
    // "Příchozí" breakdown isn't a single revenue row: operating revenue plus
    // intercompany receipts (dividends / management fees from the subsidiaries)
    // and the odd miscellaneous credit. Factors average ~1 → total inflow
    // unchanged. INTERNAL/OTHER here are credits (direction IN), distinct from
    // the outgoing intercompany funding below.
    incomeMix: [
      { weight: 8, factor: 1.05, category: "INCOME", names: ["Northpoint Capital — retainer", "Marisco Industries — services", "Client settlement", "Advisory fee", "Milestone payment", "Subscription revenue"] },
      { weight: 2, factor: 0.9, category: "INTERNAL", names: ["Contoso Studio s.r.o. — dividend", "Contoso Labs Sp. z o.o. — management fee", "Contoso Ventures GmbH — distribution", "Contoso Trade Ltd. — dividend"] },
      { weight: 1, factor: 0.5, category: "OTHER", names: ["Insurance rebate", "R&D grant incentive", "Miscellaneous credit"] },
    ],
    expenseContactIds: ["demo-contact-vexa", "demo-contact-halofield", "demo-contact-aerial-cargo", "demo-contact-mintway", "demo-contact-vanguard-accounting"],
    expenseDescriptions: ["Supplier payment", "Card purchase", "Cloud usage", "Logistics charge", "Operating expense"],
    expenseMean: 6_500,
    // Holdings is the company shown in the cockpit analytics view — spread its
    // daily spend across categories so the per-category table reads like a real
    // operating group rather than one giant "Operations" bucket. Weighted so
    // Operations stays the plurality; factors keep total spend ≈ unchanged.
    expenseMix: [
      { weight: 5, factor: 1.0, category: "OPERATIONS", names: ["Vexa Cloud — infrastructure", "Halofield Systems — identity", "Mintway Telecom — corporate tariffs", "Solune Office — supplies", "Vanguard Accounting — services", "Aurea Power — electricity"] },
      { weight: 3, factor: 1.3, category: "PRODUCTION", names: ["Northwind Components GmbH — parts", "Steelyard Supplies — raw materials", "Forge & Metric — assemblies", "Lumen Hardware — components"] },
      { weight: 2, factor: 0.9, category: "SALES", names: ["Sparrow Media — paid campaigns", "Brightframe Ads — search", "Lumio Studio — creative", "Outreach Labs — demand gen"] },
      { weight: 2, factor: 0.55, category: "TRAVEL", names: ["Aerlite Travel — flights", "Grandhotel Europa — accommodation", "CityCab — transfers", "Rail Europe — tickets"] },
      { weight: 1, factor: 0.3, category: "FINANCIAL", names: ["BAWAG — facility interest", "FXBridge — conversion fee", "Card scheme — processing fee"] },
      { weight: 1, factor: 1.5, category: "INVESTMENTS", names: ["Meridian Capital — equipment", "Officeworks — fit-out", "DataForge — hardware"] },
    ],
  },
  {
    companyId: "demo-company-studio-cz",
    currency: "CZK",
    accountHint: "Provozní",
    incomeContactIds: ["demo-contact-pelegrini-hospitality", "demo-contact-atria-health", "demo-contact-solstice-bank-partner"],
    incomeDescriptions: ["Platba klienta", "Faktura — projekt", "Záloha", "Měsíční paušál", "Dílčí plnění"],
    incomeMean: 140_000,
    expenseContactIds: ["demo-contact-aurea", "demo-contact-coastward", "demo-contact-tilly-creative"],
    expenseDescriptions: ["Platba dodavateli", "Platba kartou", "Provozní náklad", "Doprava", "Materiál"],
    expenseMean: 120_000,
  },
  {
    companyId: "demo-company-labs-pl",
    currency: "PLN",
    accountHint: "Operating",
    incomeContactIds: ["demo-contact-kepler-robotics"],
    incomeDescriptions: ["Płatność klienta", "Faktura — usługi B+R", "Rozliczenie etapu", "Abonament", "Zaliczka"],
    incomeMean: 20_000,
    expenseContactIds: ["demo-contact-tetra-drive", "demo-contact-solune"],
    expenseDescriptions: ["Płatność dostawcy", "Płatność kartą", "Koszt operacyjny", "Materiały biurowe"],
    expenseMean: 14_000,
  },
  {
    companyId: "demo-company-ventures-de",
    currency: "EUR",
    accountHint: "Operating",
    incomeContactIds: ["demo-contact-payflow-gateway", "demo-contact-brightsky-media"],
    incomeDescriptions: ["Gateway settlement", "Daily payout", "Campaign invoice", "Subscription revenue", "Client payment"],
    incomeMean: 5_400,
    expenseContactIds: ["demo-contact-brickline", "demo-contact-pixelpath", "demo-contact-aerlite-travel", "demo-contact-halo-studios"],
    expenseDescriptions: ["Supplier payment", "Card purchase", "Tooling spend", "Travel expense", "Operating expense"],
    expenseMean: 5_000,
  },
  {
    companyId: "demo-company-trade-gb",
    currency: "GBP",
    accountHint: "Operating",
    incomeContactIds: ["demo-contact-mirage-apparel"],
    incomeDescriptions: ["Client settlement", "Royalty payout", "Wholesale invoice", "Licensing fee", "Order payment"],
    incomeMean: 2_900,
    expenseContactIds: ["demo-contact-quillstream", "demo-contact-northpoint-advisors"],
    expenseDescriptions: ["Supplier payment", "Card purchase", "Professional fee", "Operating expense"],
    expenseMean: 2_600,
  },
];

function pushStreamRows(rows: DemoTransaction[], cfg: StreamConfig): void {
  const incRng = makeRng(hashSeed(cfg.companyId + "-inc"));
  const expRng = makeRng(hashSeed(cfg.companyId + "-exp"));
  let incCounter = 0;
  let expCounter = 0;

  // Expand the optional income/expense mixes into weight-proportional cycles so
  // the category distribution is deterministic and matches the configured
  // weights.
  const expandCycle = (mix: CategoryMixSlice[] | undefined) =>
    mix?.flatMap((slice) => Array.from({ length: slice.weight }, () => slice));
  const incomeCycle = expandCycle(cfg.incomeMix);
  const expenseCycle = expandCycle(cfg.expenseMix);

  // Walk the whole window day by day (~12 months ago → 1 day ago) so the
  // cockpit's month view has real history to page back through.
  for (let daysAgo = 364; daysAgo >= 1; daysAgo--) {
    // INCOME — 1–3 inflows on most days (skip ~1 in 8 days for natural gaps).
    const incToday = incRng() < 0.12 ? 0 : 1 + Math.floor(incRng() * 3); // 0..3, mostly 1-2
    for (let k = 0; k < incToday; k++) {
      if (incomeCycle && incomeCycle.length > 0) {
        // Diversified inflow — pick the income-side category slice and a
        // matching inline party (revenue client, subsidiary, etc.), credited IN.
        const slice = incomeCycle[incCounter % incomeCycle.length]!;
        const amount = niceAmount(
          cfg.incomeMean * slice.factor * (0.5 + incRng() * 1.0),
          cfg.currency,
        );
        const name = slice.names[incCounter % slice.names.length]!;
        pushInlineRow(rows, {
          companyId: cfg.companyId,
          counterpartyId: `stream-in-${slice.category.toLowerCase()}`,
          counterpartyName: name,
          amount,
          currency: cfg.currency,
          daysAgo,
          hour: 8 + (incCounter % 9),
          minute: (incCounter * 17) % 60,
          description: name.split(" — ")[1] ?? name,
          category: slice.category,
          method: "TRANSFER",
          accountHint: cfg.accountHint,
          idSuffix: `stream-inc-${incCounter}`,
          direction: "IN",
        });
        incCounter++;
        continue;
      }
      const amount = niceAmount(cfg.incomeMean * (0.45 + incRng() * 1.2), cfg.currency);
      const contactId = cfg.incomeContactIds[incCounter % cfg.incomeContactIds.length]!;
      const desc = cfg.incomeDescriptions[incCounter % cfg.incomeDescriptions.length]!;
      pushSingle(rows, {
        contactId,
        amount,
        currency: cfg.currency,
        daysAgo,
        hour: 8 + (incCounter % 9),
        minute: (incCounter * 17) % 60,
        description: desc,
        category: "INCOME",
        method: "TRANSFER",
        accountHint: cfg.accountHint,
        idSuffix: `stream-inc-${incCounter}`,
      });
      incCounter++;
    }

    // EXPENSE — 1–2 outflows on most days (skip ~1 in 6 days).
    const expToday = expRng() < 0.16 ? 0 : 1 + Math.floor(expRng() * 2); // 0..2, mostly 1
    for (let k = 0; k < expToday; k++) {
      const method: DemoTransaction["method"] = expCounter % 2 === 0 ? "CARD" : "TRANSFER";
      if (expenseCycle && expenseCycle.length > 0) {
        // Diversified mix — pick the category slice and an inline counterparty
        // that matches it (so chart, table and drill-down all stay consistent).
        const slice = expenseCycle[expCounter % expenseCycle.length]!;
        const amount = niceAmount(
          cfg.expenseMean * slice.factor * (0.5 + expRng() * 1.0),
          cfg.currency,
        );
        const name = slice.names[expCounter % slice.names.length]!;
        pushInlineRow(rows, {
          companyId: cfg.companyId,
          counterpartyId: `stream-${slice.category.toLowerCase()}`,
          counterpartyName: name,
          amount,
          currency: cfg.currency,
          daysAgo,
          hour: 9 + (expCounter % 8),
          minute: (expCounter * 23) % 60,
          description: name.split(" — ")[1] ?? name,
          category: slice.category,
          method,
          accountHint: cfg.accountHint,
          idSuffix: `stream-exp-${expCounter}`,
        });
        expCounter++;
        continue;
      }
      const amount = niceAmount(cfg.expenseMean * (0.4 + expRng() * 1.2), cfg.currency);
      const contactId = cfg.expenseContactIds[expCounter % cfg.expenseContactIds.length]!;
      const desc = cfg.expenseDescriptions[expCounter % cfg.expenseDescriptions.length]!;
      pushSingle(rows, {
        contactId,
        amount,
        currency: cfg.currency,
        daysAgo,
        hour: 9 + (expCounter % 8),
        minute: (expCounter * 23) % 60,
        description: desc,
        category: "OPERATIONS",
        method,
        accountHint: cfg.accountHint,
        idSuffix: `stream-exp-${expCounter}`,
      });
      expCounter++;
    }
  }
}

// Simple string hash → 32-bit seed.
function hashSeed(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

type SingleRow = {
  contactId: string;
  amount: number;
  currency: DemoAccountCurrency;
  daysAgo: number;
  hour: number;
  minute: number;
  description: string;
  category: DemoTransaction["category"];
  method: DemoTransaction["method"];
  accountHint: string;
  idSuffix: string;
};

function pushSingle(rows: DemoTransaction[], r: SingleRow): void {
  const cp = contact(r.contactId);
  const account = findAccount(cp.companyId, r.currency, r.accountHint);
  const direction: DemoTransaction["direction"] = r.category === "INCOME" ? "IN" : "OUT";
  const signed = direction === "IN" ? Math.abs(r.amount) : -Math.abs(r.amount);
  const bookedAt = shift(r.daysAgo, r.hour, r.minute);
  rows.push({
    id: `demo-tx-${cp.id}-${r.idSuffix}-${r.daysAgo}`,
    accountId: account.id,
    bookedAt,
    valueAt: bookedAt,
    amount: signed,
    currency: r.currency,
    description: r.description,
    variableSymbol: `2026${String(rows.length + 1000).padStart(5, "0")}`,
    constantSymbol: direction === "IN" ? "0308" : "0558",
    counterpartyId: cp.id,
    counterpartyName: cp.name,
    counterpartyAccount: cp.iban,
    counterpartyBic: cp.bankBic,
    companyId: cp.companyId,
    category: r.category,
    method: r.method,
    direction,
    status: "Booked",
  });
}

type InlineRow = {
  companyId: DemoCompanyId;
  counterpartyId: string;
  counterpartyName: string;
  amount: number;
  currency: DemoAccountCurrency;
  daysAgo: number;
  hour: number;
  minute: number;
  description: string;
  category: DemoTransaction["category"];
  method: DemoTransaction["method"];
  accountHint: string;
  idSuffix: string;
  // Override the income/expense direction. Defaults to IN for INCOME, OUT
  // otherwise — set explicitly for income-side credits in non-INCOME categories
  // (e.g. INTERNAL dividends, OTHER rebates).
  direction?: DemoTransaction["direction"];
};

// Like pushSingle but with an inline counterparty (no contact record) — used
// for synthetic flows (diversified income/expense mix, intercompany transfers)
// where the counterparty is just a label, not a managed contact.
function pushInlineRow(rows: DemoTransaction[], r: InlineRow): void {
  const account = findAccount(r.companyId, r.currency, r.accountHint);
  const direction: DemoTransaction["direction"] =
    r.direction ?? (r.category === "INCOME" ? "IN" : "OUT");
  const signed = direction === "IN" ? Math.abs(r.amount) : -Math.abs(r.amount);
  const bookedAt = shift(r.daysAgo, r.hour, r.minute);
  rows.push({
    id: `demo-tx-${r.companyId}-${r.idSuffix}-${r.daysAgo}`,
    accountId: account.id,
    bookedAt,
    valueAt: bookedAt,
    amount: signed,
    currency: r.currency,
    description: r.description,
    variableSymbol: `2026${String(rows.length + 1000).padStart(5, "0")}`,
    constantSymbol: direction === "IN" ? "0308" : "0558",
    counterpartyId: r.counterpartyId,
    counterpartyName: r.counterpartyName,
    counterpartyAccount: "—",
    companyId: r.companyId,
    category: r.category,
    method: r.method,
    direction,
    status: "Booked",
  });
}

function buildTransactions(): DemoTransaction[] {
  const rows: DemoTransaction[] = [];

  for (const cfg of STREAMS) {
    pushStreamRows(rows, cfg);
  }

  for (const sp of SPENDS) {
    const cp = contact(sp.contactId);
    sp.amounts.forEach((amount, i) => {
      const daysAgo = sp.daysAgo[i] ?? sp.daysAgo[sp.daysAgo.length - 1];
      const account = findAccount(cp.companyId, sp.currency, sp.accountHint);
      const direction: DemoTransaction["direction"] = sp.category === "INCOME" ? "IN" : "OUT";
      const signed = direction === "IN" ? Math.abs(amount) : -Math.abs(amount);
      const bookedAt = shift(daysAgo, sp.hour ?? 9 + (i % 7), (daysAgo * 11) % 60);
      rows.push({
        id: `demo-tx-${cp.id}-${i}-${daysAgo}`,
        accountId: account.id,
        bookedAt,
        valueAt: bookedAt,
        amount: signed,
        currency: sp.currency,
        description: sp.description,
        variableSymbol: `2026${String(rows.length + 1000).padStart(5, "0")}`,
        constantSymbol: direction === "IN" ? "0308" : "0558",
        counterpartyId: cp.id,
        counterpartyName: cp.name,
        counterpartyAccount: cp.iban,
        counterpartyBic: cp.bankBic,
        companyId: cp.companyId,
        category: sp.category,
        method: sp.method,
        direction,
        status: "Booked",
      });
    });
  }

  for (const p of PAYROLLS) {
    const account = findAccount(p.companyId, p.currency, "Payroll") ?? findAccount(p.companyId, p.currency);
    const bookedAt = shift(p.daysAgo, 8, 30);
    rows.push({
      id: `demo-tx-payroll-${p.companyId}-${p.daysAgo}`,
      accountId: account.id,
      bookedAt,
      valueAt: bookedAt,
      amount: -p.amount,
      currency: p.currency,
      description: p.description,
      counterpartyId: "internal-payroll",
      counterpartyName: "Team payroll — batch run",
      counterpartyAccount: "—",
      companyId: p.companyId,
      category: "WAGES",
      method: "TRANSFER",
      direction: "OUT",
      status: "Booked",
    });
  }

  for (const t of TAXES) {
    const account = findAccount(t.companyId, t.currency, "Tax") ?? findAccount(t.companyId, t.currency);
    const bookedAt = shift(t.daysAgo, 10);
    rows.push({
      id: `demo-tx-tax-${t.companyId}-${t.daysAgo}`,
      accountId: account.id,
      bookedAt,
      valueAt: bookedAt,
      amount: -t.amount,
      currency: t.currency,
      description: t.description,
      counterpartyId: "internal-fee",
      counterpartyName: "Tax authority",
      counterpartyAccount: "—",
      companyId: t.companyId,
      category: "TAXES",
      method: "TRANSFER",
      direction: "OUT",
      status: "Booked",
    });
  }

  // Intercompany funding — Contoso Holdings (the group parent shown in the
  // cockpit analytics view) periodically funds its subsidiaries from Treasury.
  // Populates the "Intercompany / Internal Transfers" category with a realistic,
  // lumpy cadence. EUR on a Holdings account so it lands in the analytics view.
  const INTERCOMPANY_TARGETS = [
    { name: "Contoso Studio s.r.o. — group funding", amount: 12_000 },
    { name: "Contoso Labs Sp. z o.o. — capital injection", amount: 9_000 },
    { name: "Contoso Ventures GmbH — group transfer", amount: 7_000 },
  ];
  let icIndex = 0;
  for (let daysAgo = 4; daysAgo <= 86; daysAgo += 11) {
    const target = INTERCOMPANY_TARGETS[icIndex % INTERCOMPANY_TARGETS.length]!;
    pushInlineRow(rows, {
      companyId: "demo-company-holdings-at",
      counterpartyId: "internal-intercompany",
      counterpartyName: target.name,
      amount: target.amount,
      currency: "EUR",
      daysAgo,
      hour: 11,
      minute: 15,
      description: "Intercompany funding",
      category: "INTERNAL",
      method: "TRANSFER",
      accountHint: "Treasury",
      idSuffix: `intercompany-${icIndex}`,
    });
    icIndex++;
  }

  // Holdings monthly tax/levy within the analytics window (payroll & corporate
  // tax advances) so the "Taxes and Duties" row is populated in the weekly view
  // too — the larger quarterly VAT lands earlier, near the window edge.
  for (const daysAgo of [6, 34, 62]) {
    pushInlineRow(rows, {
      companyId: "demo-company-holdings-at",
      counterpartyId: "internal-fee",
      counterpartyName: "Finanzamt Wien — payroll & corporate tax",
      amount: 9_200,
      currency: "EUR",
      daysAgo,
      hour: 10,
      minute: 0,
      description: "Tax advance",
      category: "TAXES",
      method: "TRANSFER",
      accountHint: "Tax",
      idSuffix: "tax-monthly",
    });
  }

  // Tiny recurring bank fees per account
  for (const account of DEMO_ACCOUNTS) {
    for (const daysAgo of [9, 29, 59, 89]) {
      const bookedAt = shift(daysAgo, 23, 30);
      rows.push({
        id: `demo-tx-fee-${account.id}-${daysAgo}`,
        accountId: account.id,
        bookedAt,
        valueAt: bookedAt,
        amount: -(account.currency === "CZK" ? 360 : account.currency === "PLN" ? 60 : 12),
        currency: account.currency,
        description: "Account maintenance fee",
        counterpartyId: "internal-fee",
        counterpartyName: "Bank fee",
        counterpartyAccount: "—",
        companyId: account.companyId,
        category: "FINANCIAL",
        method: "FEE",
        direction: "OUT",
        status: "Booked",
      });
    }
  }

  rows.sort((a, b) => (a.bookedAt < b.bookedAt ? 1 : -1));
  return rows;
}

export const DEMO_TRANSACTIONS = buildTransactions();
